import { Rng } from './rng';
import { smooth5, smooth5d } from './math';

const SIZE = 1024;
const MASK = SIZE - 1;

/**
 * Seeded value noise (1D/2D) with quintic interpolation and analytic derivatives.
 * Table-driven so it is fast enough to evaluate per grass blade in a worker.
 */
export class ValueNoise {
  private readonly perm = new Uint16Array(SIZE * 2);
  private readonly values = new Float64Array(SIZE);

  constructor(seed: number, stream: number) {
    const rng = new Rng(seed, stream, 0x5eed);
    const p = new Uint16Array(SIZE);
    for (let i = 0; i < SIZE; i++) p[i] = i;
    for (let i = SIZE - 1; i > 0; i--) {
      const j = Math.floor(rng.float() * (i + 1));
      const t = p[i]!;
      p[i] = p[j]!;
      p[j] = t;
    }
    for (let i = 0; i < SIZE * 2; i++) this.perm[i] = p[i & MASK]!;
    for (let i = 0; i < SIZE; i++) this.values[i] = rng.float() * 2 - 1;
  }

  /** 1D noise in [-1, 1]. */
  n1(x: number): number {
    const xi = Math.floor(x);
    const t = x - xi;
    const i = xi & MASK;
    const a = this.values[this.perm[i]!]!;
    const b = this.values[this.perm[i + 1]!]!;
    return a + (b - a) * smooth5(t);
  }

  /** d/dx of n1. */
  n1d(x: number): number {
    const xi = Math.floor(x);
    const t = x - xi;
    const i = xi & MASK;
    const a = this.values[this.perm[i]!]!;
    const b = this.values[this.perm[i + 1]!]!;
    return (b - a) * smooth5d(t);
  }

  /** 2D noise in [-1, 1]. */
  n2(x: number, y: number): number {
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    const tx = x - xi;
    const ty = y - yi;
    const ix = xi & MASK;
    const iy = yi & MASK;
    const p = this.perm;
    const v = this.values;
    const a = v[p[p[ix]! + iy]!]!;
    const b = v[p[p[ix + 1]! + iy]!]!;
    const c = v[p[p[ix]! + iy + 1]!]!;
    const d = v[p[p[ix + 1]! + iy + 1]!]!;
    const ux = smooth5(tx);
    const uy = smooth5(ty);
    return a + (b - a) * ux + (c - a) * uy + (a - b - c + d) * ux * uy;
  }

  /** 2D noise with analytic gradient written into out[0..1]; returns value. */
  n2g(x: number, y: number, out: Float64Array): number {
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    const tx = x - xi;
    const ty = y - yi;
    const ix = xi & MASK;
    const iy = yi & MASK;
    const p = this.perm;
    const v = this.values;
    const a = v[p[p[ix]! + iy]!]!;
    const b = v[p[p[ix + 1]! + iy]!]!;
    const c = v[p[p[ix]! + iy + 1]!]!;
    const d = v[p[p[ix + 1]! + iy + 1]!]!;
    const ux = smooth5(tx);
    const uy = smooth5(ty);
    const dux = smooth5d(tx);
    const duy = smooth5d(ty);
    const k = a - b - c + d;
    out[0] = dux * (b - a + k * uy);
    out[1] = duy * (c - a + k * ux);
    return a + (b - a) * ux + (c - a) * uy + k * ux * uy;
  }

  /** Fractal sum (octaves of n2) in roughly [-1, 1]. */
  fbm2(x: number, y: number, octaves: number, lacunarity = 2.03, gain = 0.5): number {
    let sum = 0;
    let amp = 1;
    let norm = 0;
    for (let o = 0; o < octaves; o++) {
      sum += this.n2(x, y) * amp;
      norm += amp;
      amp *= gain;
      x = x * lacunarity + 17.13;
      y = y * lacunarity - 9.71;
    }
    return sum / norm;
  }

  /** Ridged fractal in [0, 1] (sharp crests — dunes, rock strata). */
  ridged2(x: number, y: number, octaves: number): number {
    let sum = 0;
    let amp = 1;
    let norm = 0;
    for (let o = 0; o < octaves; o++) {
      const n = 1 - Math.abs(this.n2(x, y));
      sum += n * n * amp;
      norm += amp;
      amp *= 0.5;
      x = x * 2.01 + 3.7;
      y = y * 2.01 - 1.3;
    }
    return sum / norm;
  }
}
